import React, { useState, useEffect, useCallback, useRef } from 'react';
import timeTrackingService from '../services/timeTrackingService';
import TimeTrackingNav from '../components/TimeTrackingNav';

// ── Severity → points deducted ────────────────────────────────────────────────
function fmtHoursTotal(minutes) {
  if (!minutes) return '0h 0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}
function fmtDateShort(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const SEVERITY_POINTS = { minor: 1, major: 3, critical: 5 };
const CATEGORY_LABELS = {
  sanitation: 'Sanitation', product_quality: 'Product Quality', waste: 'Waste',
  customer_complaint: 'Customer Complaint', compliance: 'Compliance', other: 'Other',
  performance_target: 'Performance Target',
};
const SEVERITY_COLORS = { minor: '#ffc107', major: '#fd7e14', critical: '#dc3545' };
const currentYear = new Date().getFullYear();

// ── Employee Management Drawer ────────────────────────────────────────────────
function EmployeeManageDrawer({ employee, onClose, onRefresh }) {
  const [tab, setTab] = useState('portal');

  // Portal Access state
  const [portalStatus, setPortalStatus] = useState(null); // loaded from employee
  const [portalPassword, setPortalPassword] = useState('');
  const [portalShowPw, setPortalShowPw] = useState(false);
  const [portalMsg, setPortalMsg] = useState('');
  const [portalErr, setPortalErr] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  // Marks state
  const [markYear, setMarkYear] = useState(currentYear);
  const [marksData, setMarksData] = useState(null);
  const [marksLoading, setMarksLoading] = useState(false);
  const [markForm, setMarkForm] = useState({ category: 'sanitation', severity: 'minor', date: new Date().toISOString().split('T')[0], notes: '' });
  const [markSaving, setMarkSaving] = useState(false);
  const [markErr, setMarkErr] = useState('');

  // Events participation state
  const [eventsData, setEventsData] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState({});

  // Bonus state
  const [bonusYear, setBonusYear] = useState(currentYear);
  const [bonusData, setBonusData] = useState(null);
  const [bonusForm, setBonusForm] = useState({ bonusType: 'flat', flatAmount: '', salaryPct: '' });
  const [bonusSaving, setBonusSaving] = useState(false);
  const [bonusMsg, setBonusMsg] = useState('');
  const [bonusErr, setBonusErr] = useState('');

  useEffect(() => {
    setPortalStatus({
      enabled: !!employee.portalPasswordHash,
      locked: employee.portalLockedUntil && new Date(employee.portalLockedUntil) > new Date(),
      profileUpdated: !!employee.profileUpdatedByEmployee,
    });
  }, [employee]);

  useEffect(() => {
    if (tab !== 'marks') return;
    setMarksLoading(true);
    timeTrackingService.getEmployeeMarks(employee.id, markYear)
      .then(setMarksData).catch(() => setMarksData(null)).finally(() => setMarksLoading(false));
  }, [tab, markYear, employee.id]);

  useEffect(() => {
    if (tab !== 'events') return;
    setEventsLoading(true);
    timeTrackingService.getEmployeeEvents(employee.id)
      .then(setEventsData).catch(() => setEventsData([])).finally(() => setEventsLoading(false));
  }, [tab, employee.id]);

  useEffect(() => {
    if (tab !== 'bonus') return;
    timeTrackingService.getBonusConfig(employee.id, bonusYear)
      .then(d => {
        setBonusData(d);
        if (d.config) {
          setBonusForm({
            bonusType: d.config.bonusType || 'flat',
            flatAmount: d.config.flatAmount || '',
            salaryPct: d.config.salaryPct || '',
          });
        } else {
          setBonusForm({ bonusType: 'flat', flatAmount: '', salaryPct: '' });
        }
      }).catch(() => setBonusData(null));
  }, [tab, bonusYear, employee.id]);

  const portalAction = async (action) => {
    setPortalMsg(''); setPortalErr(''); setPortalLoading(true);
    try {
      if (action === 'enable' || action === 'reset') {
        if (!portalPassword || portalPassword.length < 6) { setPortalErr('Password must be at least 6 characters.'); setPortalLoading(false); return; }
        await (action === 'enable'
          ? timeTrackingService.enablePortalAccess(employee.id, portalPassword)
          : timeTrackingService.resetPortalPassword(employee.id, portalPassword));
        setPortalPassword('');
        setPortalStatus(s => ({ ...s, enabled: true }));
        setPortalMsg(action === 'enable' ? 'Portal access enabled.' : 'Password reset successfully.');
        onRefresh();
      } else if (action === 'unlock') {
        await timeTrackingService.unlockPortalAccount(employee.id);
        setPortalStatus(s => ({ ...s, locked: false }));
        setPortalMsg('Account unlocked.');
        onRefresh();
      } else if (action === 'profile-reviewed') {
        await timeTrackingService.markProfileReviewed(employee.id);
        setPortalStatus(s => ({ ...s, profileUpdated: false }));
        setPortalMsg('Profile changes marked as reviewed.');
        onRefresh();
      }
    } catch (e) {
      setPortalErr(e?.response?.data?.error || 'Action failed.');
    } finally { setPortalLoading(false); }
  };

  const handleAddMark = async (e) => {
    e.preventDefault();
    setMarkErr(''); setMarkSaving(true);
    try {
      await timeTrackingService.addEmployeeMark(employee.id, { ...markForm, year: markYear });
      setMarkForm(f => ({ ...f, notes: '' }));
      const d = await timeTrackingService.getEmployeeMarks(employee.id, markYear);
      setMarksData(d);
    } catch (err) {
      setMarkErr(err?.response?.data?.error || 'Failed to add mark.');
    } finally { setMarkSaving(false); }
  };

  const handleDeleteMark = async (markId) => {
    if (!window.confirm('Delete this mark?')) return;
    try {
      await timeTrackingService.deleteMark(markId);
      const d = await timeTrackingService.getEmployeeMarks(employee.id, markYear);
      setMarksData(d);
    } catch { /* ignore */ }
  };

  const handleSaveBonus = async (e) => {
    e.preventDefault();
    setBonusMsg(''); setBonusErr(''); setBonusSaving(true);
    try {
      await timeTrackingService.saveBonusConfig(employee.id, bonusYear, bonusForm);
      setBonusMsg('Bonus config saved.');
    } catch (err) {
      setBonusErr(err?.response?.data?.error || 'Save failed.');
    } finally { setBonusSaving(false); }
  };

  const TABS = [
    { key: 'portal', label: 'Portal Access' },
    { key: 'marks',  label: 'Marks & Points' },
    { key: 'bonus',  label: 'Bonus Config' },
    { key: 'events', label: 'Events' },
  ];

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 440, maxWidth: '100vw', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', zIndex: 1055, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div className="fw-bold" style={{ fontSize: 16 }}>{employee.firstName} {employee.lastName}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>{employee.payType === 'hourly' ? 'Hourly' : 'Salary'} · Badge #{employee.badgeNumber || employee.id}</div>
        </div>
        <button className="btn-close" onClick={onClose} />
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #dee2e6', padding: '0 20px' }}>
        <ul className="nav nav-tabs border-0">
          {TABS.map(t => (
            <li key={t.key} className="nav-item">
              <button className={`nav-link ${tab === t.key ? 'active' : ''}`} style={{ fontSize: 13 }} onClick={() => setTab(t.key)}>{t.label}</button>
            </li>
          ))}
        </ul>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

        {/* ── Portal Access Tab ── */}
        {tab === 'portal' && (
          <div>
            {portalMsg && <div className="alert alert-success py-2" style={{ fontSize: 13 }}>{portalMsg}</div>}
            {portalErr && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{portalErr}</div>}

            <div className="mb-3 d-flex align-items-center gap-2">
              <span className={`badge ${portalStatus?.enabled ? 'text-bg-success' : 'text-bg-secondary'}`}>
                {portalStatus?.enabled ? 'Portal Enabled' : 'Portal Disabled'}
              </span>
              {portalStatus?.locked && <span className="badge text-bg-danger">Account Locked</span>}
              {portalStatus?.profileUpdated && <span className="badge text-bg-warning text-dark">Profile Updated</span>}
            </div>

            {portalStatus?.profileUpdated && (
              <div className="alert alert-warning mb-3" style={{ fontSize: 13 }}>
                <div className="fw-semibold mb-2">Employee updated their profile — review changes below:</div>
                <table className="table table-sm mb-2" style={{ fontSize: 12, background: 'transparent' }}>
                  <tbody>
                    {[
                      ['Phone',      employee.phone],
                      ['Email',      employee.email],
                      ['Address',    employee.address],
                      ['City',       employee.city],
                      ['State',      employee.state],
                      ['ZIP',        employee.zip],
                      ['Emerg. Contact', employee.emergencyContactName],
                      ['Emerg. Phone',   employee.emergencyContactPhone],
                    ].filter(([, v]) => v).map(([label, val]) => (
                      <tr key={label}>
                        <td className="text-muted fw-semibold" style={{ whiteSpace: 'nowrap', paddingRight: 12 }}>{label}</td>
                        <td>{val}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button className="btn btn-sm btn-warning" onClick={() => portalAction('profile-reviewed')} disabled={portalLoading}>
                  Mark as Reviewed
                </button>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>
                {portalStatus?.enabled ? 'Reset Portal Password' : 'Set Initial Portal Password'}
              </label>
              <div className="input-group input-group-sm">
                <input
                  type={portalShowPw ? 'text' : 'password'}
                  className="form-control"
                  placeholder="Min 6 characters"
                  value={portalPassword}
                  onChange={e => setPortalPassword(e.target.value)}
                />
                <button className="btn btn-outline-secondary" type="button" onClick={() => setPortalShowPw(v => !v)}>{portalShowPw ? '🙈' : '👁'}</button>
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                className={`btn btn-sm ${portalStatus?.enabled ? 'btn-outline-primary' : 'btn-primary'}`}
                onClick={() => portalAction(portalStatus?.enabled ? 'reset' : 'enable')}
                disabled={portalLoading}
              >
                {portalLoading ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                {portalStatus?.enabled ? 'Reset Password' : 'Enable Portal Access'}
              </button>
              {portalStatus?.locked && (
                <button className="btn btn-sm btn-outline-danger" onClick={() => portalAction('unlock')} disabled={portalLoading}>
                  Unlock Account
                </button>
              )}
            </div>

            <hr className="my-4" />
            <div className="text-muted" style={{ fontSize: 12 }}>
              <strong>How it works:</strong> The employee logs in at <code>/portal</code> using their badge number and this portal password (separate from their clock-in PIN). On first login they must change their password.
            </div>
          </div>
        )}

        {/* ── Marks & Points Tab ── */}
        {tab === 'marks' && (
          <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fw-semibold" style={{ fontSize: 14 }}>Year</span>
              <select className="form-select form-select-sm" style={{ width: 90 }} value={markYear} onChange={e => setMarkYear(Number(e.target.value))}>
                {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            {/* Point balance summary */}
            {marksData && (
              <div className="card mb-3">
                <div className="card-body py-2 px-3 d-flex justify-content-between align-items-center">
                  <div>
                    <div style={{ fontSize: 11, color: '#6c757d', textTransform: 'uppercase', letterSpacing: 0.5 }}>Points Balance</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: marksData.currentPoints >= 90 ? '#198754' : marksData.currentPoints >= 75 ? '#e6a817' : '#dc3545', lineHeight: 1.1 }}>
                      {marksData.currentPoints}
                      <span style={{ fontSize: 13, fontWeight: 400, color: '#6c757d' }}> / {marksData.startingPoints}</span>
                    </div>
                  </div>
                  {marksData.deducted > 0 && (
                    <div className="text-end">
                      <div style={{ fontSize: 11, color: '#dc3545' }}>−{marksData.deducted} deducted</div>
                      <div style={{ fontSize: 11, color: '#6c757d' }}>{(marksData.marks || []).length} mark{(marksData.marks || []).length !== 1 ? 's' : ''}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Add mark form */}
            <div className="card mb-3 border-primary">
              <div className="card-body py-3">
                <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>Apply Derogatory Mark</div>
                {markErr && <div className="alert alert-danger py-1" style={{ fontSize: 12 }}>{markErr}</div>}
                <form onSubmit={handleAddMark}>
                  <div className="row g-2 mb-2">
                    <div className="col-7">
                      <label className="form-label" style={{ fontSize: 12 }}>Category</label>
                      <select className="form-select form-select-sm" value={markForm.category} onChange={e => setMarkForm(f => ({ ...f, category: e.target.value }))}>
                        {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div className="col-5">
                      <label className="form-label" style={{ fontSize: 12 }}>Severity</label>
                      <select className="form-select form-select-sm" value={markForm.severity} onChange={e => setMarkForm(f => ({ ...f, severity: e.target.value }))}>
                        <option value="minor">Minor (−1)</option>
                        <option value="major">Major (−3)</option>
                        <option value="critical">Critical (−5)</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label" style={{ fontSize: 12 }}>Date</label>
                      <input type="date" className="form-control form-control-sm" value={markForm.date} onChange={e => setMarkForm(f => ({ ...f, date: e.target.value }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label" style={{ fontSize: 12 }}>Notes / Justification</label>
                      <textarea className="form-control form-control-sm" rows={2} value={markForm.notes} onChange={e => setMarkForm(f => ({ ...f, notes: e.target.value }))} placeholder="Describe what happened..." />
                    </div>
                  </div>
                  <button className="btn btn-danger btn-sm w-100" type="submit" disabled={markSaving}>
                    {markSaving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                    Apply Mark (−{SEVERITY_POINTS[markForm.severity]} pts)
                  </button>
                </form>
              </div>
            </div>

            {/* Mark history */}
            {marksLoading ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" /></div>
            ) : (marksData?.marks || []).length === 0 ? (
              <p className="text-muted text-center" style={{ fontSize: 13 }}>No marks for {markYear}.</p>
            ) : (marksData?.marks || []).map(m => (
              <div key={m.id} className="card mb-2">
                <div className="card-body py-2 px-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <span className="fw-semibold" style={{ fontSize: 13 }}>{CATEGORY_LABELS[m.category]}</span>
                      <span className="ms-2 badge" style={{ fontSize: 10, background: SEVERITY_COLORS[m.severity], color: '#fff' }}>{m.severity}</span>
                      <div className="text-muted" style={{ fontSize: 11 }}>{m.date}{m.notes ? ` · ${m.notes}` : ''}</div>
                      {m.AppliedBy && <div style={{ fontSize: 11, color: '#6c757d' }}>by {m.AppliedBy.name || m.AppliedBy.email}</div>}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ fontWeight: 700, color: '#dc3545', fontSize: 13 }}>−{m.pointsDeducted}</span>
                      <button className="btn btn-link btn-sm p-0 text-danger" style={{ fontSize: 18, lineHeight: 1 }} onClick={() => handleDeleteMark(m.id)} title="Delete">×</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Bonus Config Tab ── */}
        {tab === 'bonus' && (
          <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fw-semibold" style={{ fontSize: 14 }}>Year</span>
              <select className="form-select form-select-sm" style={{ width: 90 }} value={bonusYear} onChange={e => setBonusYear(Number(e.target.value))}>
                {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            {bonusData?.policy && (
              <div className="alert alert-info py-2 mb-3" style={{ fontSize: 12 }}>
                Company point policy: starts at <strong>{bonusData.policy.startingPoints} pts</strong>. Current points: <strong>{bonusData.currentPoints}</strong>.
                {bonusData.applicableTier && <span> Applicable tier: <strong>{bonusData.applicableTier.bonusPct}%</strong></span>}
              </div>
            )}

            {bonusMsg && <div className="alert alert-success py-2" style={{ fontSize: 13 }}>{bonusMsg}</div>}
            {bonusErr && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{bonusErr}</div>}

            <form onSubmit={handleSaveBonus}>
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Bonus Type</label>
                <select className="form-select form-select-sm" value={bonusForm.bonusType} onChange={e => setBonusForm(f => ({ ...f, bonusType: e.target.value }))}>
                  <option value="flat">Flat Amount ($)</option>
                  <option value="salary_pct">Salary % (of annual salary)</option>
                  <option value="both">Both (flat + salary %)</option>
                </select>
              </div>
              {(bonusForm.bonusType === 'flat' || bonusForm.bonusType === 'both') && (
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Target Flat Bonus ($)</label>
                  <input type="number" className="form-control form-control-sm" step="0.01" min="0" placeholder="e.g. 500" value={bonusForm.flatAmount} onChange={e => setBonusForm(f => ({ ...f, flatAmount: e.target.value }))} />
                  <div className="form-text" style={{ fontSize: 11 }}>The bonus payout = flat amount × (tier bonusPct / 100)</div>
                </div>
              )}
              {(bonusForm.bonusType === 'salary_pct' || bonusForm.bonusType === 'both') && (
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: 13, fontWeight: 600 }}>Salary % Target</label>
                  <div className="input-group input-group-sm">
                    <input type="number" className="form-control" step="0.1" min="0" max="100" placeholder="e.g. 5" value={bonusForm.salaryPct} onChange={e => setBonusForm(f => ({ ...f, salaryPct: e.target.value }))} />
                    <span className="input-group-text">%</span>
                  </div>
                  <div className="form-text" style={{ fontSize: 11 }}>Applied to annual salary, scaled by tier bonusPct</div>
                </div>
              )}
              <button className="btn btn-primary btn-sm w-100" type="submit" disabled={bonusSaving}>
                {bonusSaving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                Save Bonus Config
              </button>
            </form>
          </div>
        )}

        {/* ── Events Tab ── */}
        {tab === 'events' && (
          <div>
            {eventsLoading && <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>}
            {!eventsLoading && eventsData && eventsData.length === 0 && (
              <p className="text-muted" style={{ fontSize: 13 }}>This employee has not been assigned to any events yet.</p>
            )}
            {!eventsLoading && eventsData && eventsData.length > 0 && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="text-muted" style={{ fontSize: 12 }}>{eventsData.length} event{eventsData.length !== 1 ? 's' : ''} total</span>
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {fmtHoursTotal(eventsData.reduce((s, e) => s + e.totalMinutes, 0))} across all events
                  </span>
                </div>
                <div className="d-flex flex-column gap-2">
                  {eventsData.map(ev => {
                    const expanded = eventsExpanded[ev.event.id];
                    const STATUS_COLOR = { draft: 'secondary', active: 'success', closed: 'dark' };
                    return (
                      <div key={ev.event.id} className="card">
                        <div
                          className="card-body py-2 px-3"
                          style={{ cursor: ev.days.length ? 'pointer' : 'default' }}
                          onClick={() => ev.days.length && setEventsExpanded(x => ({ ...x, [ev.event.id]: !x[ev.event.id] }))}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div style={{ flex: 1 }}>
                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-semibold" style={{ fontSize: 13 }}>{ev.event.name}</span>
                                <span className={`badge bg-${STATUS_COLOR[ev.event.status] || 'secondary'} text-capitalize`} style={{ fontSize: 10 }}>{ev.event.status}</span>
                                {ev.isInventoryManager && <span className="badge bg-info text-dark" style={{ fontSize: 10 }}>Inv. Mgr</span>}
                              </div>
                              <div className="text-muted" style={{ fontSize: 11 }}>
                                {fmtDateShort(ev.event.startDate)} – {fmtDateShort(ev.event.endDate)}
                                {ev.tags && <span className="ms-2">{ev.tags.split(',').map(t => <span key={t} className="badge bg-light text-dark border me-1" style={{ fontSize: 9 }}>{t.trim()}</span>)}</span>}
                              </div>
                            </div>
                            <div className="text-end ms-2" style={{ flexShrink: 0 }}>
                              <div className="fw-semibold" style={{ fontSize: 13 }}>{fmtHoursTotal(ev.totalMinutes)}</div>
                              <div className="text-muted" style={{ fontSize: 11 }}>{ev.totalDays} day{ev.totalDays !== 1 ? 's' : ''}</div>
                            </div>
                          </div>

                          {expanded && ev.days.length > 0 && (
                            <div className="mt-2 pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                              <table className="table table-sm mb-0" style={{ fontSize: 11 }}>
                                <thead>
                                  <tr className="text-muted">
                                    <th>Date</th>
                                    <th>Shift</th>
                                    <th className="text-end">Hours</th>
                                    <th className="text-center">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ev.days.map((d, i) => (
                                    <tr key={i}>
                                      <td>{fmtDateShort(d.date)}</td>
                                      <td className="text-muted">
                                        {d.shiftStart && d.shiftEnd
                                          ? `${d.shiftStart.slice(0,5)}–${d.shiftEnd.slice(0,5)}`
                                          : d.shiftStart ? `From ${d.shiftStart.slice(0,5)}` : '—'}
                                      </td>
                                      <td className="text-end">{d.shiftMinutes ? fmtHoursTotal(d.shiftMinutes) : '—'}</td>
                                      <td className="text-center">
                                        {d.confirmedByEmployee
                                          ? <span className="badge bg-success" style={{ fontSize: 9 }}>Confirmed</span>
                                          : d.published
                                            ? <span className="badge bg-primary" style={{ fontSize: 9 }}>Scheduled</span>
                                            : <span className="badge bg-secondary" style={{ fontSize: 9 }}>Draft</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {ev.days.length > 0 && (
                            <div className="text-muted mt-1" style={{ fontSize: 10 }}>
                              {expanded ? '▲ hide days' : `▼ show ${ev.days.length} day${ev.days.length !== 1 ? 's' : ''}`}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const EMPTY_FORM = { firstName: '', lastName: '', pin: '', payType: 'hourly', hourlyRate: '', salaryAmount: '', salaryPeriod: 'annual', defaultBreakMinutes: 60, hiredAt: '', dateOfBirth: '', ptoPolicyId: '', sickPolicyId: '', phone: '', email: '', address: '', city: '', state: '', zip: '', emergencyContactName: '', emergencyContactPhone: '' };

function tenureLabel(hiredAt) {
  if (!hiredAt) return '—';
  const ms = Date.now() - new Date(hiredAt).getTime();
  const years  = Math.floor(ms / (365.25 * 24 * 3600 * 1000));
  const months = Math.floor((ms % (365.25 * 24 * 3600 * 1000)) / (30.44 * 24 * 3600 * 1000));
  if (years === 0) return `${months}mo`;
  return `${years}y ${months}mo`;
}

function displaySalary(emp) {
  if (!emp.annualSalary) return '—';
  const annual = Number(emp.annualSalary);
  if (emp.salaryPeriod === 'monthly') return `$${(annual / 12).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo`;
  return `$${annual.toLocaleString()}/yr`;
}

export default function TimeTrackingEmployeesPage() {
  const [employees, setEmployees]       = useState([]);
  const [policies,  setPolicies]        = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [showAll, setShowAll]           = useState(false);
  const [modal, setModal]               = useState(null); // null | { mode: 'new' | 'edit', employee? }
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState('');
  const [confirmTerminate, setConfirmTerminate] = useState(null);
  const [showPin, setShowPin]                   = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [manageEmp, setManageEmp]       = useState(null); // employee being managed in drawer
  const [bonusMap, setBonusMap]         = useState({});   // employeeId → { points, bonusPayout, bonusPct }
  const manageEmpRef = useRef(null);
  useEffect(() => { manageEmpRef.current = manageEmp; }, [manageEmp]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [emps, pols, yearEnd] = await Promise.all([
        timeTrackingService.getEmployees(),
        timeTrackingService.getTimeOffPolicies().catch(() => []),
        timeTrackingService.getYearEndReport(new Date().getFullYear()).catch(() => null),
      ]);
      setEmployees(emps);
      setPolicies(pols);
      if (yearEnd?.results) {
        const map = {};
        yearEnd.results.forEach(r => { map[r.employee.id] = r; });
        setBonusMap(map);
      }
      // Keep the open drawer in sync with fresh employee data
      if (manageEmpRef.current) {
        const fresh = emps.find(e => e.id === manageEmpRef.current.id);
        if (fresh) setManageEmp(fresh);
      }
    } catch {
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_FORM); setSaveError(''); setShowPin(false); setModal({ mode: 'new' }); };
  const openEdit = (emp) => {
    const period = emp.salaryPeriod || 'annual';
    const annual = emp.annualSalary ? Number(emp.annualSalary) : null;
    const salaryAmount = annual ? (period === 'monthly' ? String(Math.round(annual / 12)) : String(annual)) : '';
    setForm({
      firstName: emp.firstName,
      lastName: emp.lastName,
      pin: '',
      payType: emp.payType,
      hourlyRate: emp.hourlyRate || '',
      salaryAmount,
      salaryPeriod: period,
      defaultBreakMinutes: emp.defaultBreakMinutes,
      hiredAt: emp.hiredAt || '',
      dateOfBirth: emp.dateOfBirth || '',
      ptoPolicyId:  emp.ptoPolicyId  ?? '',
      sickPolicyId: emp.sickPolicyId ?? '',
      phone:                emp.phone                || '',
      email:                emp.email                || '',
      address:              emp.address              || '',
      city:                 emp.city                 || '',
      state:                emp.state                || '',
      zip:                  emp.zip                  || '',
      emergencyContactName:  emp.emergencyContactName  || '',
      emergencyContactPhone: emp.emergencyContactPhone || '',
    });
    setSaveError('');
    setShowPin(false);
    setModal({ mode: 'edit', employee: emp });
  };

  const handleSave = async () => {
    setSaveError('');
    if (!form.firstName.trim() || !form.lastName.trim()) { setSaveError('First and last name are required'); return; }
    if (modal.mode === 'new' && !form.pin) { setSaveError('PIN is required'); return; }
    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        payType: form.payType,
        hourlyRate: form.payType === 'hourly' ? form.hourlyRate || null : null,
        salaryAmount: form.payType === 'salary' ? form.salaryAmount || null : null,
        salaryPeriod: form.payType === 'salary' ? form.salaryPeriod : undefined,
        defaultBreakMinutes: Number(form.defaultBreakMinutes),
        hiredAt:      form.hiredAt     || null,
        dateOfBirth:  form.dateOfBirth || null,
        ptoPolicyId:  form.ptoPolicyId  ? Number(form.ptoPolicyId)  : null,
        sickPolicyId: form.sickPolicyId ? Number(form.sickPolicyId) : null,
        phone:                form.phone                || null,
        email:                form.email                || null,
        address:              form.address              || null,
        city:                 form.city                 || null,
        state:                form.state                || null,
        zip:                  form.zip                  || null,
        emergencyContactName:  form.emergencyContactName  || null,
        emergencyContactPhone: form.emergencyContactPhone || null,
      };
      if (form.pin) payload.pin = form.pin;

      if (modal.mode === 'new') {
        await timeTrackingService.createEmployee(payload);
      } else {
        await timeTrackingService.updateEmployee(modal.employee.id, payload);
      }
      setModal(null);
      load();
    } catch (err) {
      setSaveError(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTerminate = async (emp) => {
    setActionLoading(emp.id);
    try {
      await timeTrackingService.terminateEmployee(emp.id);
      setConfirmTerminate(null);
      load();
    } catch {
      // ignore
    } finally {
      setActionLoading(null);
    }
  };

  const handleRehire = async (emp) => {
    setActionLoading(emp.id);
    try {
      await timeTrackingService.rehireEmployee(emp.id);
      load();
    } catch {
      // ignore
    } finally {
      setActionLoading(null);
    }
  };

  const visible = showAll ? employees : employees.filter(e => e.status === 'active');

  return (
    <div className="container mt-4">
      <TimeTrackingNav />
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <h5 className="mb-0">Employees</h5>
        <div className="d-flex gap-2 align-items-center">
          <div className="form-check form-switch mb-0">
            <input className="form-check-input" type="checkbox" id="showAll" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            <label className="form-check-label" htmlFor="showAll" style={{ fontSize: '0.85rem' }}>Show terminated</label>
          </div>
          <button className="btn btn-primary btn-sm" onClick={openNew}>+ New Employee</button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" /></div>
      ) : visible.length === 0 ? (
        <div className="text-muted text-center py-5">No employees found. Add your first employee to get started.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Badge #</th>
                <th>Name</th>
                <th>Status</th>
                <th>Pay Type</th>
                <th>Rate</th>
                <th>Break</th>
                <th>Hired</th>
                <th>Tenure</th>
                <th title="Current year points">Points</th>
                <th title="Estimated year-end bonus based on current points and tier">Est. Bonus</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(emp => (
                <tr key={emp.id} style={{ opacity: emp.status === 'terminated' ? 0.6 : 1 }}>
                  <td className="text-muted" style={{ fontSize: 13 }}>#{emp.badgeNumber || emp.id}</td>
                  <td className="fw-semibold">{emp.firstName} {emp.lastName}</td>
                  <td>
                    <span className={`badge ${emp.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                      {emp.status === 'active' ? 'Active' : 'Terminated'}
                    </span>
                  </td>
                  <td className="text-capitalize">{emp.payType}</td>
                  <td>
                    {emp.payType === 'hourly'
                      ? emp.hourlyRate ? `$${Number(emp.hourlyRate).toFixed(2)}/hr` : '—'
                      : displaySalary(emp)}
                  </td>
                  <td>{emp.defaultBreakMinutes} min</td>
                  <td>{emp.hiredAt || '—'}</td>
                  <td className="text-muted small">{tenureLabel(emp.hiredAt)}</td>
                  <td style={{ fontSize: 13 }}>
                    {bonusMap[emp.id] != null ? (
                      <span style={{ fontWeight: 600, color: bonusMap[emp.id].points >= 90 ? '#198754' : bonusMap[emp.id].points >= 75 ? '#e6a817' : '#dc3545' }}>
                        {bonusMap[emp.id].points}
                      </span>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {bonusMap[emp.id]?.bonusPayout != null ? (
                      <span className="fw-semibold">
                        ${Number(bonusMap[emp.id].bonusPayout).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <span className="text-muted ms-1" style={{ fontSize: 11 }}>({bonusMap[emp.id].bonusPct}%)</span>
                      </span>
                    ) : <span className="text-muted" style={{ fontSize: 12 }}>No config</span>}
                  </td>
                  <td>
                    <div className="d-flex gap-1 justify-content-end flex-wrap">
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => openEdit(emp)}>Edit</button>
                      <button
                        className={`btn btn-sm ${emp.profileUpdatedByEmployee ? 'btn-warning' : 'btn-outline-primary'}`}
                        onClick={() => setManageEmp(emp)}
                        title="Manage portal, marks & bonus"
                      >
                        {emp.profileUpdatedByEmployee ? '⚠ Manage' : 'Manage'}
                      </button>
                      {emp.status === 'active' ? (
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setConfirmTerminate(emp)}
                          disabled={actionLoading === emp.id}
                        >
                          Terminate
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-success btn-sm"
                          onClick={() => handleRehire(emp)}
                          disabled={actionLoading === emp.id}
                        >
                          Rehire
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── New / Edit Modal ─────────────────────────────────────────────── */}
      {modal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modal.mode === 'new' ? 'New Employee' : 'Edit Employee'}</h5>
                <button className="btn-close" onClick={() => setModal(null)} />
              </div>
              <div className="modal-body">
                {saveError && <div className="alert alert-danger py-2">{saveError}</div>}
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label">First Name *</label>
                    <input className="form-control" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Last Name *</label>
                    <input className="form-control" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">{modal.mode === 'new' ? 'PIN (4–6 digits) *' : 'New PIN (leave blank to keep current)'}</label>
                    <div className="input-group">
                      <input
                        className="form-control"
                        type={showPin ? 'text' : 'password'}
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={6}
                        placeholder="••••"
                        value={form.pin}
                        onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '') }))}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPin(v => !v)}
                        tabIndex={-1}
                        title={showPin ? 'Hide PIN' : 'Show PIN'}
                      >
                        {showPin ? '🙈' : '👁'}
                      </button>
                    </div>
                    {modal.mode === 'edit' && <div className="form-text">Current PIN is encrypted and cannot be displayed.</div>}
                  </div>
                  <div className="col-6">
                    <label className="form-label">Pay Type</label>
                    <select className="form-select" value={form.payType} onChange={e => setForm(f => ({ ...f, payType: e.target.value }))}>
                      <option value="hourly">Hourly</option>
                      <option value="salary">Salary</option>
                    </select>
                  </div>
                  <div className="col-6">
                    {form.payType === 'hourly' ? (
                      <>
                        <label className="form-label">Hourly Rate ($)</label>
                        <input className="form-control" type="number" step="0.01" min="0" value={form.hourlyRate} onChange={e => setForm(f => ({ ...f, hourlyRate: e.target.value }))} />
                      </>
                    ) : (
                      <>
                        <label className="form-label">
                          Salary ($) &nbsp;
                          <span className="btn-group btn-group-sm ms-1">
                            {['monthly','annual'].map(p => (
                              <button key={p} type="button"
                                className={`btn btn-sm py-0 px-2 ${form.salaryPeriod === p ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setForm(f => ({ ...f, salaryPeriod: p }))}
                              >
                                {p === 'monthly' ? '/mo' : '/yr'}
                              </button>
                            ))}
                          </span>
                        </label>
                        <input className="form-control" type="number" step="1" min="0" value={form.salaryAmount} onChange={e => setForm(f => ({ ...f, salaryAmount: e.target.value }))} placeholder={form.salaryPeriod === 'monthly' ? 'e.g. 3000' : 'e.g. 36000'} />
                      </>
                    )}
                  </div>
                  <div className="col-6">
                    <label className="form-label">Break (minutes)</label>
                    <input className="form-control" type="number" min="0" max="120" value={form.defaultBreakMinutes} onChange={e => setForm(f => ({ ...f, defaultBreakMinutes: e.target.value }))} />
                    <div className="form-text">Applied on shifts ≥ 5 hours</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Hire Date</label>
                    <input className="form-control" type="date" value={form.hiredAt} onChange={e => setForm(f => ({ ...f, hiredAt: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Date of Birth <span className="text-muted fw-normal">(optional)</span></label>
                    <input className="form-control" type="date" value={form.dateOfBirth} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} />
                  </div>
                  {policies.length > 0 && (
                    <>
                      <div className="col-6">
                        <label className="form-label">PTO Policy</label>
                        <select className="form-select" value={form.ptoPolicyId} onChange={e => setForm(f => ({ ...f, ptoPolicyId: e.target.value }))}>
                          <option value="">— None —</option>
                          {policies.filter(p => p.type === 'pto').map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label">Sick Policy</label>
                        <select className="form-select" value={form.sickPolicyId} onChange={e => setForm(f => ({ ...f, sickPolicyId: e.target.value }))}>
                          <option value="">— None —</option>
                          {policies.filter(p => p.type === 'sick').map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  <hr className="my-1" />
                  <div className="col-12 mt-2">
                    <div className="fw-semibold mb-1" style={{ fontSize: 13 }}>Contact Information</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Phone</label>
                    <input className="form-control" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Personal Email</label>
                    <input className="form-control" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Street Address</label>
                    <input className="form-control" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                  </div>
                  <div className="col-5">
                    <label className="form-label">City</label>
                    <input className="form-control" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                  </div>
                  <div className="col-3">
                    <label className="form-label">State</label>
                    <input className="form-control" maxLength={2} value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} />
                  </div>
                  <div className="col-4">
                    <label className="form-label">ZIP</label>
                    <input className="form-control" value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} />
                  </div>
                  <div className="col-12 mt-2">
                    <div className="fw-semibold mb-1" style={{ fontSize: 13 }}>Emergency Contact</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Name</label>
                    <input className="form-control" value={form.emergencyContactName} onChange={e => setForm(f => ({ ...f, emergencyContactName: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Phone</label>
                    <input className="form-control" type="tel" value={form.emergencyContactPhone} onChange={e => setForm(f => ({ ...f, emergencyContactPhone: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Employee Manage Drawer ─────────────────────────────────────────── */}
      {manageEmp && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1050 }} onClick={() => setManageEmp(null)} />
          <EmployeeManageDrawer
            employee={manageEmp}
            onClose={() => setManageEmp(null)}
            onRefresh={load}
          />
        </>
      )}

      {/* ── Terminate Confirm ──────────────────────────────────────────────── */}
      {confirmTerminate && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content">
              <div className="modal-body text-center py-4">
                <div style={{ fontSize: '2rem' }}>⚠️</div>
                <div className="fw-semibold mt-2">Terminate {confirmTerminate.firstName} {confirmTerminate.lastName}?</div>
                <div className="text-muted small mt-1">They will no longer be able to clock in. Time history is preserved.</div>
              </div>
              <div className="modal-footer justify-content-center">
                <button className="btn btn-secondary btn-sm" onClick={() => setConfirmTerminate(null)}>Cancel</button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleTerminate(confirmTerminate)}
                  disabled={actionLoading === confirmTerminate.id}
                >
                  {actionLoading === confirmTerminate.id ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                  Terminate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
